import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Building2, Users, LogOut, Layers, Star, X, Settings as SettingsIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AuthService } from '../../handling_data/services/AuthService';

interface SidebarProps {
    isOpen: boolean;
    onClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const navItems = [
        { icon: Building2, label: t('sidebar.properties'), path: '/properties' },
        { path: '/owners', icon: Users, label: t('sidebar.owners') },
        { path: '/categories', icon: Layers, label: t('sidebar.categories') },
        { path: '/settings', icon: SettingsIcon, label: t('common.settings') },
    ];

    const handleLogout = async () => {
        try {
            await AuthService.logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout failed:', error);
            navigate('/login');
        }
    };

    return (
        <aside className={`
            fixed md:relative
            w-64 bg-primary text-white h-screen flex flex-col shadow-2xl z-50
            transition-transform duration-300
            ${isOpen ? 'translate-x-0' : (i18n.language === 'ar' ? 'translate-x-full' : '-translate-x-full')}
            md:translate-x-0 flex-shrink-0
        `}>
            {/* Brand */}
            <div
                className="h-20 flex items-center justify-between px-6 border-b border-white/10 cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => navigate('/properties')}
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-accent" />
                    </div>
                    <h1 className="text-xl font-serif font-bold tracking-wider whitespace-nowrap">{t('common.brand_name')}</h1>
                </div>

                {/* Mobile Close Button */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    className="p-2 -mr-2 text-white/70 hover:text-white md:hidden"
                >
                    <X className="w-6 h-6" />
                </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto custom-scrollbar">
                {navItems.map((item) => (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        onClick={onClose}
                        className={({ isActive }: { isActive: boolean }) =>
                            `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${isActive
                                ? 'bg-accent text-white shadow-lg shadow-accent/20 font-bold'
                                : 'text-gray-300 hover:bg-white/5 hover:text-white'
                            }`
                        }
                    >
                        <item.icon className="w-5 h-5 transition-colors" />
                        <span className="text-sm">{item.label}</span>
                    </NavLink>
                ))}
            </nav>

            {/* User / Logout */}
            <div className="p-4 border-t border-white/10">
                <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-red-300 hover:bg-red-500/10 hover:text-red-200 transition-colors"
                >
                    <LogOut className="w-5 h-5" />
                    <span className="text-sm font-medium">{t('common.logout')}</span>
                </button>
            </div>
        </aside>
    );
};
